/**
 * i18n key of the data-confirmation warning shown when a publisher's data has
 * not been confirmed within the last year.
 */
export var DATA_CONFIRMATION_WARNING_MESSAGE_ID = "ui-br-publishers.my-publisher.warning.message";
/**
 * i18n key of the banner shown when the publisher already has an open SUBMITTED
 * update request pending approval.
 */
export var UPDATE_REQUEST_EXISTS_MESSAGE_ID = "ui-br-publishers.publishers.update.request.exists.message";
/**
 * Decides whether a publisher's data confirmation is expired.
 *
 * A missing `dataConfirmationTime` on an already-loaded publisher counts as
 * expired (the publisher has never confirmed their data). Callers MUST only act
 * on this result once the publisher data is actually loaded — see
 * [resolvePublisherWarning] and its `dataLoaded` gate — otherwise a transient
 * `undefined` during loading would be misread as "expired".
 *
 * @param dataConfirmationTime ISO timestamp of the last data confirmation, or
 *   `null`/`undefined` when never confirmed.
 * @return `true` when the confirmation is missing or older than one year.
 */
export var isDataConfirmationExpired = function (dataConfirmationTime) {
    if (!dataConfirmationTime)
        return true;
    var oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return new Date(dataConfirmationTime) < oneYearAgo;
};
/**
 * Pure resolver for the "My publisher" warning banner and its error modal.
 *
 * DB-133: the warning must NOT be evaluated until the underlying data (current
 * user + publisher) has loaded. While `dataLoaded` is `false` the banner and
 * the error modal stay hidden, which prevents the warning from flashing on
 * every entry into the Publishers module (the queries use `staleTime: 0`, so a
 * fresh loading state occurs on each visit).
 *
 * @param params.dataLoaded whether both the current user and the publisher have
 *   finished loading.
 * @param params.hasOpenSubmittedRequest whether an open SUBMITTED update request
 *   exists (takes precedence over the confirmation warning, without a modal).
 * @param params.dataConfirmationExpired result of [isDataConfirmationExpired]
 *   for the loaded publisher.
 * @return the resolved [PublisherWarningState].
 */
export var resolvePublisherWarning = function (params) {
    var dataLoaded = params.dataLoaded, hasOpenSubmittedRequest = params.hasOpenSubmittedRequest, dataConfirmationExpired = params.dataConfirmationExpired;
    if (!dataLoaded) {
        return { show: false, errorModalOpen: false };
    }
    if (hasOpenSubmittedRequest) {
        return {
            show: true,
            messageId: UPDATE_REQUEST_EXISTS_MESSAGE_ID,
            errorModalOpen: false,
        };
    }
    if (dataConfirmationExpired) {
        return {
            show: true,
            messageId: DATA_CONFIRMATION_WARNING_MESSAGE_ID,
            errorModalOpen: true,
        };
    }
    return { show: false, errorModalOpen: false };
};
